import { Link } from "react-router-dom";
import { Primary, STATUS_LABEL, StatusPill, Tag, day, money } from "@/components/backoffice/kit";

const PROOF = { waiting: ["Proof waiting", "ink"], approved: ["Proof approved", "green"], changes: ["Changes asked", "red"] };

/** The one status/action line a card or the detail screen shows (8.2): next button, waiting text or 13thav's status. */
export function NextAction({ order, onAdvance, busy, full = false, size = "card" }) {
    if (order.status === "cancelled") return <span className="text-[14px] text-[#767676]" data-testid={`next-${order.id}`}>Cancelled</span>;
    if (order.prints === "consignment") {
        return <span className="text-[14px] text-[#4d4d4d]" data-testid={`next-${order.id}`}>13thav: {order.maker_status ? (order.maker_status === "in_production" ? "Printing" : order.maker_status.replace("_", " ")) : "received"}</span>;
    }
    if (order.prints === "shop") {
        return <span className="text-[14px] text-[#4d4d4d]" data-testid={`next-${order.id}`}>{order.shop_name || "The shop"} prints · {STATUS_LABEL[order.status] || order.status}</span>;
    }
    if (order.status === "pending_payment") return <span className="text-[14px] font-medium text-[#b3261e]" data-testid={`next-${order.id}`}>Waiting for payment</span>;
    if (!order.next) return <span className="text-[14px] text-[#4d4d4d]" data-testid={`next-${order.id}`}>Done</span>;
    return (
        <Primary onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAdvance(order, order.next.status); }} disabled={busy}
            testid={`next-${order.id}`} full={full} className={size === "card" ? "min-h-[44px]" : ""}>
            {order.next.label}
        </Primary>
    );
}

export function Badges({ order, testid }) {
    const proof = PROOF[order.proof_badge];
    return (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={testid}>
            <StatusPill status={order.status} testid={`${testid}-status`} />
            <Tag tone={order.prints === "consignment" ? "ink" : "grey"} testid={`${testid}-prints`}>
                {order.prints === "consignment" ? "13thav prints" : order.prints === "mixed" ? "Mixed" : order.prints === "shop" ? "Shop prints" : order.shop_name ? "13thav prints" : "You print"}
            </Tag>
            {proof && <Tag tone={proof[1]} testid={`${testid}-proof`}>{proof[0]}</Tag>}
            {order.order_type === "reprint" && <Tag tone="grey" testid={`${testid}-reprint`}>Reprint</Tag>}
            {order.balance_due > 0 && <Tag tone="red" testid={`${testid}-balance`}>Balance {money(order.balance_due)}</Tag>}
        </div>
    );
}

export default function OrderCard({ order, to, onAdvance, busy }) {
    return (
        <Link to={to} data-testid={`order-card-${order.id}`}
            className="grid h-full grid-cols-[72px_minmax(0,1fr)] gap-3 border border-[#1a1a1a] bg-white p-3 sm:grid-cols-[96px_minmax(0,1fr)_11rem]">
            <div className="h-[72px] w-[72px] border border-[#767676] bg-white sm:h-24 sm:w-24">
                {order.thumb ? <img src={order.thumb} alt="" className="h-full w-full object-contain" />
                    : <span className="flex h-full items-center justify-center text-[11px] text-[#767676]">no image</span>}
            </div>
            <div className="min-w-0">
                <p className="flex flex-wrap items-baseline gap-x-2 text-[16px]">
                    <span className="font-semibold" data-testid={`order-code-${order.id}`}>{order.order_number}</span>
                    <span className="text-[#4d4d4d]">{order.first_name || "Customer"}</span>
                    {order.shop_name && <span className="text-[14px] text-[#4d4d4d]" data-testid={`order-shop-${order.id}`}>· {order.shop_name}</span>}
                    <span className="ml-auto font-semibold tabular-nums" data-testid={`order-total-${order.id}`}>{money(order.shop_total)}</span>
                </p>
                {order.prints !== "self" && !order.shop_name && <p className="text-[13px] text-[#4d4d4d]" data-testid={`order-keep-${order.id}`}>You keep {money(order.my_total)}</p>}
                <p className="truncate text-[16px]" data-testid={`order-headline-${order.id}`}>{order.headline}</p>
                <p className="flex flex-wrap items-center gap-x-2 text-[14px] text-[#4d4d4d]">
                    {order.ready_by && <span data-testid={`order-ready-${order.id}`}>Ready by {day(order.ready_by)}</span>}
                    {order.need_by && (
                        <Tag tone={order.need_by_late ? "red" : "ink"} testid={`order-needby-${order.id}`}>Need by {day(order.need_by)}</Tag>
                    )}
                </p>
                {order.note && <p className="truncate text-[14px] italic text-[#4d4d4d]" data-testid={`order-note-${order.id}`}>“{order.note.split("\n")[0]}”</p>}
                <div className="mt-1"><Badges order={order} testid={`order-badges-${order.id}`} /></div>
            </div>
            <div className="col-span-2 flex items-center sm:col-span-1 sm:justify-end sm:self-center sm:text-right">
                <NextAction order={order} onAdvance={onAdvance} busy={busy} full />
            </div>
        </Link>
    );
}
